import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { LupaIcon, ShoppingCartIcon, AddCircleIcon } from "~/theme/assets";
import { defineMessages, FormattedMessage, FormattedNumber, injectIntl } from "react-intl";
import constants from "../../utils/constants";
import CurrencyDropdown from "../CurrencyDropdown/CurrencyDropdown";
import LanguagesDropdown from "../LanguagesDropdown/LanguagesDropdown";
import styles from "./Menu.scss";
import "./Menu.scss";

const msgs = defineMessages({
  availability: {
    id: "app.menu.availability",
    description: "availability",
    defaultMessage: "Availability",
  },
  bookings: {
    id: "app.menu.bookings",
    description: "bookings",
    defaultMessage: "Bookings",
  },
  addBooking: {
    id: "app.menu.addBooking",
    description: "Add booking",
    defaultMessage: "Add booking",
  },
  map: {
    id: "app.menu.map",
    description: "Map",
    defaultMessage: "Map",
  },
  advantages: {
    id: "app.menu.advantages",
    description: "Advantages",
    defaultMessage: "Advantages",
  },
});

function getLocalePath(locale, defaultLocale) {
  if (!locale || locale === defaultLocale) {
    return "";
  }
  return `/${locale}`;
}

function Menu({
  hotelTicker,
  propertyType,
  iframeMode,
  defaultLocale,
  reservations,
  availableCurrencies,
  onChangeConfig,
  onChangeLocale,
  currency,
  availableLocales,
  locale,
  currentStep,
  pushState,
  bookingTotalRate,
  showCurrency,
  showLanguage,
  onMapClick,
  showMapButton,
  fullWidth,
  startDate,
  endDate,
  totalPromotions,
  handleExpandPromotions,
  intl: { formatDate },
}) {
  return (
    <nav className={classnames("wbkv9-Menu", "row", "navigation")}>
      <div
        onClick={event =>
          pushState(
            event,
            `/${hotelTicker}${getLocalePath(locale, defaultLocale)}${iframeMode ? "?witif=1" : ""}`
          )
        }
        className={classnames(
          "wbkv9-Menu-linkContainer",
          "wbkv9-Menu-availabilityContainer",
          currentStep === constants.STEP_PATHS.STEP_2 && styles.addMenuContainer,
          styles.availabilityContainer,
          "linkContainer",
          {
            active: currentStep === constants.STEP_PATHS.STEP_1,
            disabled: currentStep === constants.STEP_PATHS.STEP_3,
          }
        )}>
        <li className={currentStep === constants.STEP_PATHS.STEP_2 && "addMenu"}>
          <div
            className={classnames(
              "wbkv9-Menu-icon",
              "icon",
              currentStep === constants.STEP_PATHS.STEP_2 && "addIcon"
            )}>
            {(currentStep !== constants.STEP_PATHS.STEP_2 && <LupaIcon />) || <AddCircleIcon />}
          </div>

          <span className={classnames("wbkv9-Menu-availabilityText", "availabilityText")}>
            {(currentStep !== constants.STEP_PATHS.STEP_2 && (
              <div
                className={classnames(
                  "wbkv9-Menu-availabilityContainer",
                  styles.availabilityContainer
                )}>
                <FormattedMessage {...msgs.availability} />

                {fullWidth && startDate && endDate && (
                  <div className={classnames("wbkv9-Menu-datesContainer", styles.datesContainer)}>
                    {startDate && (
                      <span>
                        {formatDate(startDate, { day: "2-digit" })}{" "}
                        {formatDate(startDate, { month: "short" }).substring(0, 3)}
                      </span>
                    )}

                    {" - "}

                    {endDate && (
                      <span>
                        {formatDate(endDate, { day: "2-digit" })}{" "}
                        {formatDate(endDate, { month: "short" }).substring(0, 3)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )) || <FormattedMessage {...msgs.addBooking} />}
          </span>
        </li>
      </div>

      {fullWidth && currentStep === constants.STEP_PATHS.STEP_1 && showMapButton && (
        <div
          className={classnames(
            "wbkv9-Menu-mapContainer",
            "wbkv9-Menu-defaultMenuContainer",
            "linkContainer",
            styles.mapContainer,
            styles.defaultMenuContainer
          )}
          onClick={onMapClick}>
          <div
            className={classnames(
              "wbkv9-Menu-mapText",
              "wbkv9-Menu-defaultMenuText",
              styles.mapText,
              styles.defaultMenuText
            )}>
            <FormattedMessage {...msgs.map} />
          </div>
        </div>
      )}

      {(fullWidth || propertyType === constants.PROPERTY_TYPE.HOTEL) && (
        <div className={classnames("wbkv9-Menu-itemsBlock", styles.itemsBlock)}>
          {fullWidth && totalPromotions > 0 && (
            <div
              className={classnames(
                "wbkv9-Menu-promotionsContainer",
                "wbkv9-Menu-defaultMenuContainer",
                "linkContainer",
                styles.promotionsContainer,
                styles.defaultMenuContainer
              )}
              onClick={handleExpandPromotions}>
              <div
                className={classnames(
                  "wbkv9-Menu-promotionsText",
                  "wbkv9-Menu-defaultMenuText",
                  styles.promotionsText,
                  styles.defaultMenuText
                )}>
                <FormattedMessage {...msgs.advantages} />
              </div>

              <div className={classnames("wbkv9-Menu-totalPromotions", styles.totalPromotions)}>
                {totalPromotions}
              </div>
            </div>
          )}

          {propertyType === constants.PROPERTY_TYPE.HOTEL && (
            <div
              onClick={event =>
                pushState(
                  event,
                  `/${hotelTicker}${getLocalePath(locale, defaultLocale)}/checkout${
                    iframeMode ? "?witif=1" : ""
                  }`,
                  reservations <= 0
                )
              }
              className={classnames(
                "wbkv9-Menu-linkContainer",
                "linkContainer",
                "wbkv9-Menu-shoppingCartContainer",
                styles.shoppingCartContainer,
                {
                  disabled: reservations <= 0,
                  enabled: reservations > 0,
                  active: reservations > 0 && currentStep === constants.STEP_PATHS.STEP_2,
                  animated: reservations > 0 && currentStep === constants.STEP_PATHS.STEP_1,
                }
              )}>
              <li>
                <div
                  className={classnames(
                    "wbkv9-Menu-icon",
                    "wbkv9-Menu-shoppingCart",
                    "icon",
                    "shoppingCart",
                    styles.shoppingCart
                  )}>
                  <ShoppingCartIcon />
                  {fullWidth && (
                    <div
                      className={classnames(
                        "bookingMenuReservations",
                        reservations > 0 && "enabled",
                        reservations > 0 && currentStep === constants.STEP_PATHS.STEP_2 && "active"
                      )}>
                      {reservations}
                    </div>
                  )}
                </div>
                <div
                  className={classnames(
                    "wbkv9-Menu-bookingMenu",
                    "bookingMenu",
                    styles.bookingMenu
                  )}>
                  <span className="bookingMenuText">
                    <FormattedMessage {...msgs.bookings} />
                  </span>
                  {!fullWidth && reservations > 0 && (
                    <span>
                      <span className="bookingSeparator">&#187;</span>
                      <span className="bookingMenuPrice">
                        <FormattedNumber
                          value={bookingTotalRate}
                          minimumFractionDigits={2}
                          maximumFractionDigits={2}
                        />{" "}
                      </span>
                      <span className="bookingMenuNumber">({reservations})</span>
                      <span className="bookingMenuCurrency">{currency}</span>
                      <div
                        className={classnames(
                          "bookingMenuReservations",
                          reservations > 0 && "enabled",
                          reservations > 0 &&
                            currentStep === constants.STEP_PATHS.STEP_2 &&
                            "active"
                        )}>
                        {reservations}
                      </div>
                    </span>
                  )}

                  {fullWidth && (
                    <div>
                      <div
                        className={classnames(
                          "bookingMenuPrice",
                          "hasDecimals",
                          styles.bookingMenuPrice,
                          styles.hasDecimals
                        )}>
                        <FormattedNumber
                          value={bookingTotalRate || 0}
                          minimumFractionDigits={bookingTotalRate > 0 && 2}
                          maximumFractionDigits={bookingTotalRate > 0 && 2}
                        />{" "}
                      </div>
                      <div
                        className={classnames(
                          "bookingMenuPrice",
                          "hasNoDecimals",
                          styles.bookingMenuPrice,
                          styles.hasNoDecimals
                        )}>
                        <FormattedNumber
                          value={bookingTotalRate || 0}
                          minimumFractionDigits={0}
                          maximumFractionDigits={0}
                        />{" "}
                      </div>
                      <div className="bookingMenuCurrency">{currency}</div>
                    </div>
                  )}
                </div>
              </li>
            </div>
          )}
        </div>
      )}
      {(showCurrency || showLanguage) && (
        <div className={classnames("wbkv9-Menu-dropdownMenu", "row end-xs", "DropdownMenu")}>
          {showCurrency && (
            <CurrencyDropdown
              value={currency}
              currencies={availableCurrencies}
              onCurrencyChange={chosenCurrency => onChangeConfig({ currency: chosenCurrency })}
            />
          )}
          {showLanguage && (
            <LanguagesDropdown
              value={locale}
              onLanguageChange={language => onChangeLocale({ locale: language })}
              languages={availableLocales}
            />
          )}
        </div>
      )}
    </nav>
  );
}

Menu.propTypes = {
  hotelTicker: PropTypes.string.isRequired,
  locale: PropTypes.string,
  defaultLocale: PropTypes.string,
  propertyType: PropTypes.string,
};

Menu.defaultProps = {
  defaultLocale: "EN",
};

export default injectIntl(Menu);
